import { useMemo } from 'react';
import { produce } from 'immer';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { toDictionary } from '@bbkAdminRedux/redux_utils';
import {
  bbkApiSlice,
  type BBKRequestContextArgs,
} from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { DEFAULT_BRAND_ID } from '@bbkAdminRedux/rtkq/brands.slice';
import { type RestFunnelPlacement } from '@bbkAdminRedux/rtkq/placements.utils';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { type Dictionary } from '@bbkAdminUtils/utility-types';
import {
  BrightbackCategoryOrder,
  BrightbackClassOrder,
} from '@bbkFunnel/components/survey/reasons/categories';

export const NO_MATCHING_REASON_INTERNAL_NAME = 'no_matching_reason';

export const isCustomReason = (reason: Reason) =>
  reason.internal_name === NO_MATCHING_REASON_INTERNAL_NAME;

export const isReasonForCurrentBrand = (
  brandId: string,
  vr: VanityReason,
  isMultiBrand: boolean,
  showMultiBrandEnchantment?: boolean
): boolean => {
  if (showMultiBrandEnchantment) return true;
  return isMultiBrand
    ? vr.shared ||
        (brandId === DEFAULT_BRAND_ID ? !vr.brand_id : vr.brand_id === brandId)
    : true;
};

export const isReasonFromAnotherBrand = (
  brandId: string,
  vr: VanityReason,
  isMultiBrand: boolean
): boolean => {
  return (
    isMultiBrand &&
    (!vr.shared && brandId === DEFAULT_BRAND_ID
      ? !!vr.brand_id
      : vr.brand_id !== brandId)
  );
};

export enum ReasonAction {
  NEW = 'new',
  SHARED = 'shared',
  EDIT = 'edit',
}

type GetReasonsArgs = {
  companyInternalName: string;
  appKey: string;
  brandId?: string;
  /** Locale used to resolve translated `display_name`s in the response. */
  locale?: string;
} & BBKRequestContextArgs;

export type ReasonClass = string;

export type ReasonCategory = {
  id: number;
  display_name: string;
  reason_class: ReasonClass;
};

export type Reason = {
  id: number;
  category_id: number;
  display_name: string;
  internal_name: string;
  reason_category: ReasonCategory;
};

/** Per-locale translations of a vanity reason, keyed by locale code (e.g. `de`). */
export type VanityReasonTranslations = Record<string, { display_name: string }>;

export type VanityReason = {
  id: number;
  active: boolean;
  company_id: number;
  reason_id: number;
  brand_id?: string;
  reason: Reason;
  internal_name: string;
  display_name: string;
  /** Base-locale admin-authored label for the reason's optional free-text input. */
  reason_feedback_label?: string;
  external_reason_key?: string;
  shared: boolean;
  translations?: VanityReasonTranslations;

  // Meta info injected by UI components
  hasExternalReasonKeyChanged?: boolean;
  isNew?: boolean;
  toSave?: boolean;
  showSharedModal?: boolean;
  /** Translation-only edit; saved via the translations endpoint, not `toSave`. */
  translationsChanged?: boolean;
};

type CreateVanityReasonDto = Pick<
  VanityReason,
  | 'reason_id'
  | 'display_name'
  | 'reason_feedback_label'
  | 'shared'
  | 'brand_id'
  | 'translations'
>;
export type UpdateVanityReasonDto = Pick<
  VanityReason,
  | 'id'
  | 'reason_id'
  | 'display_name'
  | 'reason_feedback_label'
  | 'shared'
  | 'brand_id'
  | 'translations'
>;

export type SaveVanityReasonDto = CreateVanityReasonDto | UpdateVanityReasonDto;

export const isUpdateVanityReasonDto = (
  vr: SaveVanityReasonDto
): vr is UpdateVanityReasonDto => 'id' in vr;

export type ReasonsResponse = {
  brightback_reasons: Reason[];
  vanity_reasons: VanityReason[];
};

type SaveVanityReasonsArgs = {
  companyInternalName: string;
  appKey: string;
  vanityReasons: SaveVanityReasonDto[];
};

type DeleteReasonArgs = {
  companyInternalName: string;
  appKey: string;
  vanityReasonId: number;
};

export const surveyReasonsSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getReasons: build.query<ReasonsResponse, GetReasonsArgs>({
      query: ({ companyInternalName, appKey, context, brandId, locale }) => ({
        url: `/api/v1/company/${companyInternalName}/app/${appKey}/reasons`,
        params: { active: false, context, brandId, locale },
      }),
      providesTags: ['Reasons'],
    }),
    getVanityReasonTranslations: build.query<
      VanityReasonTranslations,
      { companyInternalName: string; appKey: string; vanityReasonId: number }
    >({
      query: ({ companyInternalName, appKey, vanityReasonId }) =>
        `/api/v1/company/${companyInternalName}/app/${appKey}/reasons/${vanityReasonId}/translations`,
      providesTags: (result, error, { vanityReasonId }) => [
        { type: 'ReasonTranslations', id: vanityReasonId },
      ],
    }),
    upsertVanityReasonTranslations: build.mutation<
      VanityReasonTranslations,
      {
        companyInternalName: string;
        appKey: string;
        vanityReasonId: number;
        translations: VanityReasonTranslations;
      }
    >({
      query: ({
        companyInternalName,
        appKey,
        vanityReasonId,
        translations,
      }) => ({
        url: `/api/v1/company/${companyInternalName}/app/${appKey}/reasons/${vanityReasonId}/translations`,
        method: 'PUT',
        body: translations,
      }),
      invalidatesTags: (result, error, { vanityReasonId }) => [
        { type: 'ReasonTranslations', id: vanityReasonId },
        { type: 'Experience', id: 'ALL' },
      ],
    }),
    saveVanityReasons: build.mutation<VanityReason[], SaveVanityReasonsArgs>({
      query: ({ companyInternalName, appKey, ...body }) => ({
        url: `/api/v1/company/${companyInternalName}/app/${appKey}/reasons`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg, meta) => {
        return [{ type: 'Reasons' }, { type: 'Experience', id: 'ALL' }];
      },
    }),
    deleteVanityReason: build.mutation<void, DeleteReasonArgs>({
      query: ({ companyInternalName, appKey, vanityReasonId }) => ({
        url: `/api/v1/company/${companyInternalName}/app/${appKey}/vanity_reason/${vanityReasonId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Reasons'],
    }),
  }),
});

export const useSelectAllReasons = (args: GetReasonsArgs): Reason[] => {
  const { data } = surveyReasonsSlice.endpoints.getReasons.useQuery(args);
  return useMemo(() => data?.brightback_reasons ?? [], [data]);
};

export const useSelectAllReasonsEntities = (args: GetReasonsArgs) => {
  const reasons = useSelectAllReasons(args);
  return useMemo(() => toDictionary(reasons), [reasons]);
};

export const useSelectAllReasonsCategories = (
  args: GetReasonsArgs
): ReasonCategory[] => {
  const reasons = useSelectAllReasons(args);
  const categories = useMemo(
    () =>
      _.uniqBy(
        reasons.map((r) => r.reason_category),
        (rc) => rc.id
      ),
    [reasons]
  );
  return useMemo(
    () =>
      _.sortBy(categories, (x) => {
        const idx = BrightbackCategoryOrder.indexOf(x.display_name);
        return idx !== -1 ? idx : categories.length;
      }),
    [categories]
  );
};

export const useSelectAllReasonsClasses = (
  args: GetReasonsArgs
): ReasonClass[] => {
  const categories = useSelectAllReasonsCategories(args);
  const classes = useMemo(
    () =>
      _.uniqBy(
        categories.map((r) => r.reason_class),
        (rc) => rc
      ),
    [categories]
  );
  return useMemo(
    () =>
      _.sortBy(classes, (x) => {
        const idx = BrightbackClassOrder.indexOf(x);
        return idx !== -1 ? idx : classes.length;
      }),
    [classes]
  );
};

export const useSelectAllVanityReasons = (
  args: GetReasonsArgs
): VanityReason[] => {
  const { data } = surveyReasonsSlice.endpoints.getReasons.useQuery(args);
  return useMemo(() => data?.vanity_reasons ?? [], [data]);
};

export const useSelectAllVanityReasonsEntities = (args: GetReasonsArgs) => {
  const vanityReasons = useSelectAllVanityReasons(args);
  return useMemo(() => toDictionary(vanityReasons), [vanityReasons]);
};

export const useSelectActiveVanityReasons = (args: GetReasonsArgs) => {
  const vanityReasons = useSelectAllVanityReasons(args);
  return useMemo(
    () => vanityReasons.filter((vr) => vr.active),
    [vanityReasons]
  );
};

export const useSelectActiveVanityReasonsEntities = (args: GetReasonsArgs) => {
  const vanityReasons = useSelectActiveVanityReasons(args);
  return useMemo(() => toDictionary(vanityReasons), [vanityReasons]);
};

export const createANewVanityReason = (
  reason: Reason,
  brandId: string,
  isMultiBrand: boolean
): VanityReason => {
  const newReason = {
    id: uuidv4(),
    isNew: true,
    reason_id: reason.id,
    display_name: isCustomReason(reason) ? '' : reason.display_name,
    reason,
    shared: isMultiBrand ? false : true,
  } as unknown as VanityReason;
  if (isMultiBrand && brandId !== DEFAULT_BRAND_ID) {
    newReason.brand_id = brandId;
  }
  return newReason;
};

export const remapUpdatedVanityReasonsToPlacements = (
  vanityReasonsToUpdate: VanityReason[],
  updatedVanityReasons: SaveVanityReasonDto[],
  funnelsPlacementsMap: Dictionary<RestFunnelPlacement[]>
): Dictionary<RestFunnelPlacement[]> => {
  // Go through to get any local reasons which need to be updated
  const localReasonsToUpdate = vanityReasonsToUpdate.filter((r) => r.isNew);

  // update values in the placements map
  return produce(funnelsPlacementsMap, (draft) => {
    localReasonsToUpdate.forEach((r) => {
      Object.entries(draft).forEach(([fId, funnelPlacements]) => {
        const placement = funnelPlacements?.find(
          (p) => p.vanity_reason_id === r.id
        );
        if (!placement) return;

        const createdVanityReason = updatedVanityReasons
          .filter(isUpdateVanityReasonDto)
          .find(
            (uvr) =>
              uvr.reason_id === r.reason_id &&
              uvr.display_name === r.display_name
          );
        if (!createdVanityReason) {
          GlobalLogger.error(
            `Could not find created vanity reason in the response ${vanityReasonsToUpdate} ${updatedVanityReasons}`
          );
          return;
        }
        placement.vanity_reason_id = createdVanityReason.id;
      });
    });
  });
};

/**
 * Key a reason's `reason_feedback_enabled` entry. The map is persisted by `internal_name`, but the
 * server does not generate one until the reason is saved, so an unsaved reason is keyed by its
 * temporary local id (the same id its placements use) and remapped by
 * {@link remapReasonFeedbackEnabledKeys} once the save response arrives.
 */
export const reasonFeedbackEnabledKey = (
  vr: Pick<VanityReason, 'id' | 'internal_name'>
): string => vr.internal_name || String(vr.id);

/**
 * Drops entries for reasons that are no longer placed on the page. Placed reasons are matched on
 * {@link reasonFeedbackEnabledKey}, so a reason that is placed but not yet saved keeps its entry
 * under its temporary id rather than being dropped for having no `internal_name` yet.
 */
export const pruneReasonFeedbackEnabledKeys = (
  placedReasons: VanityReason[],
  reasonFeedbackEnabled: Record<string, boolean> | undefined
): Record<string, boolean> | undefined => {
  if (!reasonFeedbackEnabled) {
    return reasonFeedbackEnabled;
  }
  const placedKeys = new Set(placedReasons.map(reasonFeedbackEnabledKey));
  return Object.fromEntries(
    Object.entries(reasonFeedbackEnabled).filter(([reasonKey]) =>
      placedKeys.has(reasonKey)
    )
  );
};

/**
 * Replaces the temporary-id keys of newly created reasons with the `internal_name` the server
 * generated for them, matching created reasons by natural key the way
 * {@link remapUpdatedVanityReasonsToPlacements} does.
 */
export const remapReasonFeedbackEnabledKeys = (
  vanityReasonsToUpdate: VanityReason[],
  savedVanityReasons: VanityReason[],
  reasonFeedbackEnabled: Record<string, boolean> | undefined
): Record<string, boolean> | undefined => {
  if (!reasonFeedbackEnabled) {
    return reasonFeedbackEnabled;
  }
  const internalNameByTemporaryKey = new Map<string, string>();
  vanityReasonsToUpdate
    .filter((vr) => vr.isNew)
    .forEach((vr) => {
      const temporaryKey = reasonFeedbackEnabledKey(vr);
      if (!(temporaryKey in reasonFeedbackEnabled)) {
        return;
      }
      const createdVanityReason = savedVanityReasons.find(
        (saved) =>
          saved.reason_id === vr.reason_id &&
          saved.display_name === vr.display_name
      );
      if (!createdVanityReason?.internal_name) {
        GlobalLogger.error(
          `Could not resolve internal_name for created vanity reason ${vr.reason_id} ${vr.display_name}`
        );
        return;
      }
      internalNameByTemporaryKey.set(
        temporaryKey,
        createdVanityReason.internal_name
      );
    });
  if (internalNameByTemporaryKey.size === 0) {
    return reasonFeedbackEnabled;
  }
  return Object.fromEntries(
    Object.entries(reasonFeedbackEnabled).map(([key, enabled]) => [
      internalNameByTemporaryKey.get(key) ?? key,
      enabled,
    ])
  );
};
